import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { MessageCircle, Send, X, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const MilesChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => `pub_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  // Show welcome message on first open
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: "agent",
        content: "Yo what's good! I'm Miles, the Community Engagement Director here at My Biz Consulting. Whether you're looking to learn about our services, check out our programs, or just need some guidance on your business — I got you. What can I help you with?"
      }]);
    }
  }, [isOpen]);

  const sendMessage = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: msg }]);
    setSending(true);

    try {
      const res = await fetch(`${API}/api/miles/public-chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: sessionId })
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, { role: "agent", content: data.response }]);
      } else {
        setMessages(prev => [...prev, { role: "agent", content: "Yo, I hit a snag there. Try again in a sec!" }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: "agent", content: "Connection issue — hit me up again in a moment!" }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-[#333333] hover:bg-[#444444] text-white rounded-full shadow-lg flex items-center justify-center z-[9999] transition-all hover:scale-105 group"
          data-testid="miles-chat-button"
          aria-label="Chat with Miles"
        >
          <MessageCircle className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#39FF14] rounded-full animate-pulse" />
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-[380px] h-[520px] bg-white rounded-2xl shadow-2xl border border-neutral-200 flex flex-col z-[9999] overflow-hidden" data-testid="miles-chat-panel">
          {/* Header */}
          <div className="bg-[#333333] p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-[#39FF14] flex-shrink-0">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/23cc3132-5564-4dc3-9937-bde34e47f086/images/16f4e7cecf4ad6f1e23ead81bc93225446a64cfc7157ab92ccd668ffeb99bea2.png"
                alt="Miles"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1">
              <p className="text-white font-semibold text-sm">Miles</p>
              <p className="text-[#39FF14] text-xs">Community Engagement Director</p>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-neutral-400 hover:text-white transition-colors" data-testid="miles-chat-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-neutral-50">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-3.5 py-2.5 text-sm leading-relaxed ${
                  msg.role === "user"
                    ? "bg-[#333333] text-white rounded-2xl rounded-br-md"
                    : "bg-white text-neutral-800 rounded-2xl rounded-bl-md shadow-sm border border-neutral-100"
                }`}>
                  {msg.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm border border-neutral-100">
                  <Loader2 className="w-4 h-4 animate-spin text-[#39FF14]" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-neutral-200 bg-white">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                placeholder="Ask Miles anything..."
                className="flex-1 px-3.5 py-2.5 bg-neutral-100 rounded-full text-sm border-0 outline-none focus:ring-2 focus:ring-[#39FF14]/40"
                disabled={sending}
                data-testid="miles-chat-input"
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || sending}
                className="w-9 h-9 bg-[#333333] hover:bg-[#444444] text-white rounded-full flex items-center justify-center disabled:opacity-40 transition-colors"
                data-testid="miles-chat-send"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] text-neutral-400 text-center mt-1.5">Powered by My Biz Consulting AI</p>
          </div>
        </div>
      )}
    </>
  );
};

export default MilesChatWidget;
